import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "./App.css";
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate, useLocation, useSearchParams, Outlet } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { API } from "./config/api";
import { STAFF } from "./config/staffConfig";
import { errorMessage } from "./utils/errorMessage";
import { isClientFacingPath } from "./config/clientFacingRoutes";
import ClientLinkExpired, { ClientLinkExpiredOverlay } from "./components/ClientLinkExpired";

// Pages
import LoginPage, { POST_LOGIN_REDIRECT_KEY } from "./pages/LoginPage";
import AuthComplete from "./pages/AuthComplete";
import Dashboard from "./pages/Dashboard";
import NewMatter from "./pages/NewMatter";
import InvoiceRequest from "./pages/InvoiceRequest";
import RelinkInvoiceRequests from "./pages/finance/RelinkInvoiceRequests";
import ClientInvoices from "./pages/finance/ClientInvoices";
import FeeEarnerRates from "./pages/admin/FeeEarnerRates";
import MyTime from "./pages/MyTime";
import ConnectClaude from "./pages/ConnectClaude"; // BM-H4253
import AddExpense from "./pages/AddExpense";
import AllTimeEntries from "./pages/AllTimeEntries";
import PaymentRequest from "./pages/PaymentRequest";
import FileClosureForm from "./pages/FileClosureForm";
import SubmissionDetail from "./pages/SubmissionDetail";
import ConflictsSearch from "./pages/ConflictsSearch";
import PreserveQueryNavigate from "./components/PreserveQueryNavigate";
import BonsaiMailOnline from "./pages/BonsaiMailOnline";
import MobileDiagnostic from "./pages/MobileDiagnostic";
import ConflictCheckHealth from "./pages/ConflictCheckHealth";
// BM-H452 Phase 1 — read only practice area client register discovery
import ClientRegisterDiscovery from "./pages/ClientRegisterDiscovery";
import PreCaseMinder from "./pages/PreCaseMinder";
import RiskAssessment from "./pages/RiskAssessment";
import FoRFCombined from "./pages/FoRFCombined";
import CompletionStatementPage from "./pages/CompletionStatementPage";
import PostCompletionPage from "./pages/PostCompletionPage";
import AccountsInstructionPage from "./pages/AccountsInstructionPage";
import SendSofQuestionnairePage from "./pages/SendSofQuestionnairePage";
import ClientOnboarding from "./pages/ClientOnboarding";
import JointClientOnboarding from "./pages/JointClientOnboarding";
import TermsAgreement from "./pages/TermsAgreement";
import DirectorsLounge from "./pages/DirectorsLounge";
import ConflictApprovalPage from "./pages/ConflictApprovalPage";
import ConflictCheckDetail from "./pages/ConflictCheckDetail";
import PostIntakeQueue from "./pages/PostIntakeQueue";
import PostIntakeDetail from "./pages/PostIntakeDetail";
// Brief 14 PR2 — MLCO Inbox UI
import MLCOInboxPage from "./pages/MLCOInboxPage";
// Brief 14d — MLCO + COLP compliance dashboard
import ComplianceDashboard from "./pages/compliance/ComplianceDashboard";
// BM-H632 — file reviews (universal file review form)
import FileReviewRegister from "./pages/compliance/FileReviewRegister";
import Complaints from "./pages/compliance/Complaints"; // BM-H3370
import ReportComplaint from "./pages/compliance/ReportComplaint"; // BM-H3370
import ReportSuspicion from "./pages/compliance/ReportSuspicion"; // BM-H3819
import RegisterBook from "./pages/compliance/RegisterBook"; // BM-H3613
import Training, { TrainingOverview } from "./pages/compliance/Training"; // BM-H3630
import HorizonScan from "./pages/compliance/HorizonScan"; // BM-H3302
import ChangePack from "./pages/compliance/ChangePack"; // BM-H3980
import ProcessChanges from "./pages/compliance/ProcessChanges"; // policy change to process change, 24 Oct 2026
import FileReviewForm from "./pages/compliance/FileReviewForm";
// ComplianceMinder MVP — feature-flagged carve-out module (namespaced /mvp)
import CMFlagGuard from "./pages/compliance/mvp/CMFlagGuard";
import FileSnapHub from "./pages/filesnap/FileSnapHub";
import FileSnapFlagGuard from "./pages/filesnap/FileSnapFlagGuard";
import CMDashboard from "./pages/compliance/mvp/ComplianceDashboard";
import CMPolicyLibrary from "./pages/compliance/mvp/PolicyLibrary";
import CMPolicyViewer from "./pages/compliance/mvp/PolicyViewer";
import CMRegisterList from "./pages/compliance/mvp/RegisterList";
import CMRegisterDetail from "./pages/compliance/mvp/RegisterDetail";
import CMBundleBuilder from "./pages/compliance/mvp/BundleBuilder";
import CMCompetenceLog from "./pages/compliance/mvp/CompetenceLog"; // BM-H4201
import MatterAiWorkspace from "./pages/aiworkspace/MatterAiWorkspace";
import MatterDetail from "./pages/MatterDetail";
import OutgoingPostQueue from "./pages/OutgoingPostQueue";
import OutgoingPostDetail from "./pages/OutgoingPostDetail";
import PostHub from "./pages/PostHub";
import OpenMatters from "./pages/OpenMatters";
import IntakePreopen from "./pages/IntakePreopen";
import SDLTForm from "./pages/SDLTForm";
import SigningPage from "./pages/SigningPage";
import LoaSigningPage from "./pages/LoaSigningPage"; // BM-H3851
import SignatureManagement from "./pages/SignatureManagement";
import ClientPortal from "./pages/ClientPortal";
import ClientDocRequestPage from "./pages/ClientDocRequestPage";
import SOPPage from "./pages/SOPPage";
import GuidesLibrary from "./pages/GuidesLibrary";
import GuideReader from "./pages/GuideReader";
import PreExchangeChecklist from "./pages/PreExchangeChecklist";
import PreCompletionChecklist from "./pages/PreCompletionChecklist";
import RefinanceCompletionChecklist from "./pages/RefinanceCompletionChecklist";
import CallNoteModal from "./components/CallNoteModal";
import { resolveCallNoteMatterRef, getCurrentMatterRef } from "./lib/callNoteMatter";
import SaveMatterNoteModal from "./components/SaveMatterNoteModal";
import ThirdPartyInvoices from "./pages/ThirdPartyInvoices";
import InvoiceTracker from "./pages/InvoiceTracker";
import PaymentTracker from "./pages/PaymentTracker";
import CreditControl from "./pages/CreditControl";
import CreditControlRecord from "./pages/CreditControlRecord";
import TrustAccountsReport from "./pages/TrustAccountsReport";
import ClientAccount from "./pages/ClientAccount"; // BM-H4699
import CompletionsBookDashboard from "./pages/CompletionsBookDashboard";
import CompletionsBookForm from "./pages/CompletionsBookForm";
import DeadlineTracker from "./pages/DeadlineTracker";
import SupervisionBoard from "./pages/SupervisionBoard"; // BM-H5047
import Reports from "./pages/Reports";
import AdminTools from "./pages/AdminTools";
import SystemStatus from "./pages/SystemStatus";
import BonsaiMailFailureConsole from "./pages/BonsaiMailFailureConsole";
import OnboardingLinkHealth from "./pages/OnboardingLinkHealth";
import RefFixerPage from "./pages/RefFixerPage";
import ClientFixerPage from "./pages/ClientFixerPage";
import OperatorConsole from "./pages/OperatorConsole";
import ContactDetailPage from "./pages/ContactDetailPage";
import ContactsDirectory from "./pages/ContactsDirectory";
import FindDocument from "./pages/documents/FindDocument";
import BonsaiPen from "./pages/BonsaiPen";
import BonsaiPenTcs from "./pages/BonsaiPenTcs";
import BonsaiPenDocs from "./pages/BonsaiPenDocs";
import LatePaymentCalculator from "./pages/LatePaymentCalculator"; // BM-H4482
import LatePaymentLetters from "./pages/LatePaymentLetters"; // BM-H4482
import BonsaiPenClientLink from "./pages/BonsaiPenClientLink";
import SourceOfFundsForm from "./pages/SourceOfFundsForm";
import TA6IntakeForm from "./pages/TA6IntakeForm";
import TA7IntakeForm from "./pages/TA7IntakeForm";
import TA10IntakeForm from "./pages/TA10IntakeForm";
// BM-H80 P2 - Client-facing wills questionnaire + fee-earner review
import ClientFormWills from "./pages/ClientFormWills";
import FeeEarnerFormReview from "./pages/FeeEarnerFormReview";
// BM-H81 P2 - Client-facing LPA questionnaire
import ClientFormLPA from "./pages/ClientFormLPA";
// BM-H82 P0 - Client-facing Estate Planning Review workbook
import ClientWorkbook from "./pages/ClientWorkbook";
import ClientArticle4Questionnaire from "./pages/ClientArticle4Questionnaire";
import EPRCorrection from "./pages/EPRCorrection";
import AppLayout from "./components/AppLayout";
import PipelineTracker from "./pages/PipelineTracker";
import ActionsPage from "./pages/ActionsPage";
import BonsaiAgents from "./pages/BonsaiAgents";
import OBEmailLog from "./components/OBEmailLog";
import SentEmailLog from "./pages/SentEmailLog";
// BM-H77 Phase-2 — Quoting Tool frontend surfaces.
import Quotes from "./pages/Quotes";
import QuoteBuilder from "./pages/QuoteBuilder";
import QuoteView from "./pages/QuoteView";

// Bonsai Mail Pages
import { BonsaiChat, BonsaiRules, BonsaiActivity, BonsaiOutlookPane, BonsaiOutlookCallback, BonsaiDeadlines, DeadlineDirectors, FilingCompliance, WeeklyReport, TimeDashboard, DeletedSubmissions } from "./pages/bonsai";
import PolicyLibrary from "./pages/policy/PolicyLibrary";
import PolicyReader from "./pages/policy/PolicyReader";
import MLCOInbox from "./pages/policy/MLCOInbox";
import RegistersForms from "./pages/policy/RegistersForms";
import RegisterFormDetail from "./pages/policy/RegisterFormDetail";
import MyPolicies from "./pages/policy/MyPolicies";
import NotificationForms from "./pages/policy/NotificationForms";
import { BonsaiProvider } from "./contexts/BonsaiContext";
import { ErrorBoundary } from "./components/ErrorBoundary";

// FIX: catch must return false — returning true incorrectly activates MemoryRouter
// and prevents BonsaiMail from opening in its own browser window.
function isOutlookIframe() {
  try {
    return (
      window.self !== window.top ||
      window.location.pathname.includes("/bonsai/outlook-preview") ||
      (typeof Office !== "undefined" && Office.context)
    );
  } catch (e) {
    // Cross-origin access throws — but that does NOT mean we are inside Outlook.
    // Only the explicit pathname check or Office API presence should trigger MemoryRouter.
    return window.location.pathname.includes("/bonsai/outlook-preview");
  }
}

const AppRouter = isOutlookIframe() ? MemoryRouter : BrowserRouter;

function getMemoryInitialEntries() {
  try {
    return [window.location.pathname + window.location.search];
  } catch (e) {
    return ["/bonsai/outlook-preview"];
  }
}

// Derive role from staffConfig — single source of truth
function resolveUserRole(userId) {
  if (!userId) return null;
  const email = userId.includes("@") ? userId.toLowerCase() : null;
  if (!email) return null;
  const staff = STAFF.find(s => s.email === email);
  return (staff?.is_active && staff?.dashboard_role) || null;
}

// Brief 77 Drift 1 — Conflict Search is the single new-matter funnel. A cold
// "New Matter" entry (no continuation params) is redirected into it. The
// param'd continuation (FORF / matter-details after a conflict check) still
// renders the NewMatter wizard so existing deep-links keep working.
function NewMatterEntry() {
  const [searchParams] = useSearchParams();
  const hasContinuation =
    searchParams.get("conflict_check_id") ||
    searchParams.get("conflict_id") ||
    searchParams.get("matter_reference") ||
    searchParams.get("provisional_id") ||
    searchParams.get("submission_id") ||
    searchParams.get("conflict_done");
  if (!hasContinuation) {
    return <Navigate to="/conflict-search" replace />;
  }
  return <NewMatter />;
}

function CatchAllRedirect() {
  const location = useLocation();
  // BM-H604: a mistyped or truncated client link (e.g. /portal/ with the
  // token cut off by a mail client) must not fall through to the staff
  // login. Client-facing paths get the plain client message instead.
  const clientPath = isClientFacingPath(location.pathname);

  useEffect(() => {
    if (clientPath) return;
    const params = new URLSearchParams(location.search);
    const authSuccess = params.get("auth");
    const bonsaiUserId = params.get("bonsai_user_id");
    
    if (authSuccess === "success" && bonsaiUserId) {
      sessionStorage.setItem("bonsai_user_id", bonsaiUserId);
      localStorage.setItem("bonsai_user_id", bonsaiUserId);
      localStorage.setItem("bonsai_auth", "true");
      localStorage.setItem("bonsai_last_activity", Date.now().toString());
      window.location.href = "/dashboard";
      return;
    }
    
    const cookieMatch = document.cookie.match(/bonsai_user_id=([^;]+)/);
    if (cookieMatch) {
      sessionStorage.setItem("bonsai_user_id", cookieMatch[1]);
      localStorage.setItem("bonsai_user_id", cookieMatch[1]);
      window.location.href = "/dashboard";
      return;
    }
    
    window.location.href = "/login";
  }, [location, clientPath]);

  if (clientPath) return <ClientLinkExpired />;
  return null;
}

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const authStatus = localStorage.getItem("bonsai_auth") === "true";
    const lastActivity = localStorage.getItem("bonsai_last_activity");
    
    if (authStatus && lastActivity) {
      const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
      if (timeSinceActivity > SESSION_TIMEOUT_MS) {
        localStorage.removeItem("bonsai_auth");
        localStorage.removeItem("bonsai_last_activity");
        return false;
      }
    }
    return authStatus;
  });

  // userRole derived from staffConfig — resolved once on mount and kept in sync
  const [userRole, setUserRole] = useState(() => {
    const uid =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return resolveUserRole(uid);
  });
  
  const timeoutRef = useRef(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const updateActivity = useCallback(() => {
    if (isAuthenticated) {
      localStorage.setItem("bonsai_last_activity", Date.now().toString());
    }
  }, [isAuthenticated]);

  const handleSessionExpiry = useCallback(() => {
    setIsAuthenticated(false);
    setUserRole(null);
    setSessionExpired(true);
    localStorage.removeItem("bonsai_auth");
    localStorage.removeItem("bonsai_last_activity");
    toast.warning("Session expired due to inactivity. Please log in again.", {
      duration: 5000,
    });
  }, []);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (isAuthenticated) {
      updateActivity();
      timeoutRef.current = setTimeout(handleSessionExpiry, SESSION_TIMEOUT_MS);
    }
  }, [isAuthenticated, handleSessionExpiry, updateActivity]);

  useEffect(() => {
    if (isAuthenticated) {
      updateActivity();
      resetTimeout();

      const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      const handleActivity = () => { resetTimeout(); };

      activityEvents.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
      });

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleActivity);
        });
      };
    }
  }, [isAuthenticated, resetTimeout, updateActivity]);

  // Issue #226: the shared-password fallback has been removed from
  // POST /auth/login. All logins must carry an email; LoginPage owns
  // the form and the API call. AuthProvider now only exposes session
  // state — the call site no longer invokes this helper.
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        setIsAuthenticated(true);
        setSessionExpired(false);
        localStorage.setItem("bonsai_auth", "true");
        localStorage.setItem("bonsai_last_activity", Date.now().toString());
        const uid =
          sessionStorage.getItem("bonsai_user_id") ||
          localStorage.getItem("bonsai_user_id") ||
          "";
        setUserRole(resolveUserRole(uid));
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: errorMessage(error, "Invalid email or password"),
      };
    }
    return { success: false, message: "Login failed" };
  };

  const logout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsAuthenticated(false);
    setUserRole(null);
    setSessionExpired(false);
    localStorage.removeItem("bonsai_auth");
    localStorage.removeItem("bonsai_last_activity");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userRole, login, logout, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

// BM-H532 (15 Sep 2026): stash the intended destination before bouncing an
// unauthenticated hit to /login, using the same POST_LOGIN_REDIRECT_KEY /
// takePostLoginRedirect mechanism LoginPage.jsx already exposes. Previously
// this component did a blind <Navigate to="/login" replace /> with no memory
// of where the user was headed, so every protected-route email deep link
// (e.g. /matter/:matterRef) dropped the user on the dashboard after login.
// ConflictApprovalPage.jsx worked around this for itself only (BM-H267) by
// manually stashing before redirecting — that bespoke stash is untouched and
// now redundant-but-harmless for that one page, while every other protected
// route (present and future) gets the same behaviour for free from here.
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  // BM-H604: no client-facing path is ever behind ProtectedRoute today. If
  // one is ever nested here by mistake, the client sees the plain message,
  // never the staff Microsoft login.
  if (!isAuthenticated && isClientFacingPath(location.pathname)) {
    return <ClientLinkExpired />;
  }
  if (!isAuthenticated) {
    try {
      const intendedPath = location.pathname + location.search + location.hash;
      window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, intendedPath);
    } catch {
      // Storage unavailable (private mode) — sign-in still works, the user
      // just lands on the dashboard fallback instead of their original page.
    }
    return <Navigate to="/login" replace />;
  }
  return children;
};

function GlobalCallNote() {
  const [open, setOpen] = useState(false);
  const [overrideMatterRef, setOverrideMatterRef] = useState('');
  const [initialTab, setInitialTab] = useState('attendance');  // Brief 72: support open-tel-message
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'T' || e.key === 't')) {
        e.preventDefault();
        if (isAuthenticated) { setOverrideMatterRef(''); setInitialTab('attendance'); setOpen(true); }
      }
    };
    const customHandler = (e) => {
      if (isAuthenticated) {
        setOverrideMatterRef(e.detail?.matterRef || '');
        setInitialTab('attendance');
        setOpen(true);
      }
    };
    // Brief 72: open-tel-message opens the modal with Tel Message tab pre-selected
    const telMsgHandler = (e) => {
      if (isAuthenticated) {
        setOverrideMatterRef(e.detail?.matterRef || '');
        setInitialTab('message');
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    window.addEventListener('open-call-note', customHandler);
    window.addEventListener('open-tel-message', telMsgHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('open-call-note', customHandler);
      window.removeEventListener('open-tel-message', telMsgHandler);
    };
  }, [isAuthenticated]);

  // BM-H4109: the top bar sends no matter, so take it from the URL or from
  // the page that registered its matter (matter page, FORF page).
  const matterRef = resolveCallNoteMatterRef({
    override: overrideMatterRef,
    pathname: location.pathname,
    search: location.search,
    registered: getCurrentMatterRef(),
  });

  if (!open) return null;
  return <CallNoteModal open={open} onClose={() => { setOpen(false); setOverrideMatterRef(''); setInitialTab('attendance'); }} defaultMatterRef={matterRef} initialTab={initialTab} />;
}

function GlobalFileNote() {
  const [open, setOpen] = useState(false);
  const [overrideMatterRef, setOverrideMatterRef] = useState('');
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && (e.key === 'N' || e.key === 'n')) {
        e.preventDefault();
        if (isAuthenticated) { setOverrideMatterRef(''); setOpen(true); }
      }
    };
    const customHandler = (e) => {
      if (isAuthenticated) {
        setOverrideMatterRef(e.detail?.matterRef || '');
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    window.addEventListener('open-file-note', customHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('open-file-note', customHandler);
    };
  }, [isAuthenticated]);

  // BM-H4109: the top bar sends no matter, so take it from the URL or from
  // the page that registered its matter (matter page, FORF page).
  const matterRef = resolveCallNoteMatterRef({
    override: overrideMatterRef,
    pathname: location.pathname,
    search: location.search,
    registered: getCurrentMatterRef(),
  });

  const getHeaders = () => {
    const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
    return uid ? { "X-Bonsai-User-Id": uid } : {};
  };

  const userName = sessionStorage.getItem('bonsai_user_name') || localStorage.getItem('bonsai_user_name') || '';
  const userId = sessionStorage.getItem('bonsai_user_id') || localStorage.getItem('bonsai_user_id') || '';

  if (!open) return null;
  return (
    <SaveMatterNoteModal
      open={open}
      onOpenChange={(v) => { if (!v) { setOpen(false); setOverrideMatterRef(''); } }}
      matterReference={matterRef}
      noteType="general"
      bodyMarkdown=""
      sourceContext={{}}
      userName={userName}
      userId={userId}
      getHeaders={getHeaders}
      t={(k) => k}
    />
  );
}

// BM-H604: covers a client-facing page with the plain client message when
// the axios interceptor reports a SESSION_EXPIRED 401 there (instead of
// redirecting to the staff login, which it no longer does on these paths).
function ClientLinkExpiredGate() {
  const location = useLocation();
  return <ClientLinkExpiredOverlay pathname={location.pathname} />;
}

function App() {
  const routerProps = AppRouter === MemoryRouter
    ? { initialEntries: getMemoryInitialEntries() }
    : {};

  return (
    <AuthProvider>
      <ErrorBoundary>
      <div className="App min-h-screen bg-transparent">
        <Toaster position="top-right" richColors />
        <AppRouter {...routerProps}>
          <GlobalCallNote />
          <GlobalFileNote />
          <ClientLinkExpiredGate />
          <Routes>
            {/* ── Public / standalone routes (no sidebar) ── */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth-complete" element={<AuthComplete />} />

            {/* BM-H8 — mobile 'Failed to fetch' self-diagnostic (unauthenticated) */}
            <Route path="/diagnostic" element={<MobileDiagnostic />} />
            <Route path="/mobile-diagnostic" element={<MobileDiagnostic />} />

            {/* Bonsai Mail Outlook pane — SPA PREVIEW ONLY.
                The real add-in is served from backend/bonsai/outlook/static/outlook.html
                and uses the vanilla JS bundle at outlook-app.js (built by build.sh).
                BM-H P0-1 (01/09/2026): route renamed from /bonsai/outlook to make
                this unambiguous. See BonsaiOutlookPane.jsx banner. */}
            <Route path="/bonsai/outlook-preview" element={<BonsaiProvider><ErrorBoundary><BonsaiOutlookPane /></ErrorBoundary></BonsaiProvider>} />
            <Route path="/bonsai/outlook/callback" element={<BonsaiProvider><BonsaiOutlookCallback /></BonsaiProvider>} />

            {/* Token-gated client-facing routes */}
            <Route path="/client-onboarding" element={<ClientOnboarding />} />
            <Route path="/client-onboarding/joint/:token" element={<JointClientOnboarding />} />
            {/* BM-H469: terms only, for a client whose onboarding was linked or skipped */}
            <Route path="/terms/:token" element={<TermsAgreement />} />
            <Route path="/conflict-approval/:token" element={<ConflictApprovalPage />} />
            <Route path="/sdlt/:token" element={<SDLTForm />} />
            <Route path="/sign/:token" element={<SigningPage />} />
            {/* BM-H129 -- alias so signing emails sent before the /signing/->/sign/ fix still land on the signing page instead of a dead "can't reach this page". */}
            <Route path="/signing/:token" element={<SigningPage />} />
            {/* BM-H3851: Letter of Authority, signed by the client then the authorised person */}
            <Route path="/loa/:token" element={<LoaSigningPage />} />
            <Route path="/portal/:token" element={<ClientPortal />} />
            <Route path="/sof/:token" element={<SourceOfFundsForm />} />
            <Route path="/bonsai-pen/client/:token" element={<BonsaiPenClientLink />} />
            <Route path="/ta6/:token" element={<TA6IntakeForm />} />
            <Route path="/ta7/:token" element={<TA7IntakeForm />} />
            <Route path="/ta10/:token" element={<TA10IntakeForm />} />

            {/* BM-H80 P2 - Client-facing wills questionnaire (tokenised) */}
            <Route path="/client-forms/wills/:form_instance_id" element={<ClientFormWills />} />
            <Route path="/client-forms/wills/:form_instance_id/thanks" element={<ClientFormWills />} />

            {/* BM-H81 P2 - Client-facing LPA questionnaire (tokenised) */}
            <Route path="/client-forms/lpas/:form_instance_id" element={<ClientFormLPA />} />
            <Route path="/client-forms/lpas/:form_instance_id/thanks" element={<ClientFormLPA />} />

            {/* BM-H82 P0 - Client-facing Estate Planning Review workbook (tokenised) */}
            <Route path="/client-forms/eprs/:form_instance_id" element={<ClientWorkbook />} />
            <Route path="/client-forms/eprs/:form_instance_id/correct" element={<EPRCorrection />} />
            <Route path="/client-forms/eprs/:form_instance_id/thanks" element={<ClientWorkbook />} />

            {/* Article 4 initial review, part 3 - client questionnaire (tokenised) */}
            <Route path="/client-forms/article4s/:form_instance_id" element={<ClientArticle4Questionnaire />} />

            {/* ── Internal staff routes (with sidebar via AppLayout) ── */}
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              {/* Home */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Matters */}
              <Route path="/matters" element={<OpenMatters />} />
              <Route path="/intake-preopen" element={<IntakePreopen />} />
              <Route path="/post-intake" element={<PostIntakeQueue />} />
              <Route path="/post-intake/:postId" element={<PostIntakeDetail />} />
              {/* Brief 14 PR2 — MLCO Inbox */}
              <Route path="/compliance/mlco-inbox" element={<MLCOInboxPage />} />
              <Route path="/compliance/mlco-inbox/:id" element={<MLCOInboxPage />} />
              {/* Brief 14d — MLCO + COLP compliance dashboard */}
              <Route path="/compliance/dashboard" element={<ComplianceDashboard />} />
              {/* BM-H632 — file reviews */}
              <Route path="/compliance/file-reviews" element={<FileReviewRegister />} />
              <Route path="/compliance/complaints" element={<Complaints />} />
              <Route path="/complaints/new" element={<ReportComplaint />} />
              <Route path="/mlro/report-suspicion" element={<ReportSuspicion />} />
              <Route path="/compliance/registers" element={<RegisterBook />} />
              <Route path="/compliance/registers/:ref" element={<RegisterBook />} />
              <Route path="/training" element={<Training />} />
              <Route path="/training/:courseId" element={<Training />} />
              <Route path="/training-overview" element={<TrainingOverview />} />
              <Route path="/compliance/horizon-scan" element={<HorizonScan />} />
              <Route path="/compliance/change-pack" element={<ChangePack />} />
              <Route path="/compliance/process-changes" element={<ProcessChanges />} />
              <Route path="/compliance/file-reviews/:id" element={<FileReviewForm />} />
              {/* ComplianceMinder MVP — feature-flagged carve-out module. Each
                  route is wrapped in CMFlagGuard (redirects when the flag is
                  off), namespaced under /compliance/mvp to avoid colliding with
                  the Wave 1 compliance screens above. */}
              <Route path="/compliance/mvp" element={<CMFlagGuard><CMDashboard /></CMFlagGuard>} />
              <Route path="/compliance/mvp/policies" element={<CMFlagGuard><CMPolicyLibrary /></CMFlagGuard>} />
              <Route path="/compliance/mvp/policies/:slug" element={<CMFlagGuard><CMPolicyViewer /></CMFlagGuard>} />
              <Route path="/compliance/mvp/registers" element={<CMFlagGuard><CMRegisterList /></CMFlagGuard>} />
              <Route path="/compliance/mvp/registers/:slug" element={<CMFlagGuard><CMRegisterDetail /></CMFlagGuard>} />
              <Route path="/compliance/mvp/bundles" element={<CMFlagGuard><CMBundleBuilder /></CMFlagGuard>} />
              <Route path="/compliance/mvp/competence" element={<CMFlagGuard><CMCompetenceLog /></CMFlagGuard>} />
              <Route path="/matter/:matterRef/ai-workspace" element={<MatterAiWorkspace />} />
              {/* Brief 77 Drift 3/4/5 — the matter detail screen the guide
                  refers to ("from the matter screen…"). The page component
                  existed but was never wired into the router; without this the
                  FE-input / finance surfaces are unreachable. */}
              <Route path="/matter/:matterRef" element={<MatterDetail />} />
              {/* BM-H80 P2 - Fee-earner client-form review surface */}
              <Route path="/matters/:matterRef/client-form-review/:form_instance_id" element={<FeeEarnerFormReview />} />
              <Route path="/outgoing-post" element={<OutgoingPostQueue />} />
              <Route path="/outgoing-post/:postId" element={<OutgoingPostDetail />} />
              <Route path="/post-hub" element={<PostHub />} />
              <Route path="/deadlines" element={<DeadlineTracker />} />
              <Route path="/supervision" element={<SupervisionBoard />} />
              <Route path="/pipeline" element={<BonsaiProvider><PipelineTracker /></BonsaiProvider>} />

              {/* Actions */}
              <Route path="/actions" element={<ActionsPage />} />
              {/* BM-H620 — Bonsai Agents (the Reviews page). The route always
                  exists; hub.reviews_feature.require_reviews_page_enabled
                  404s the API behind it while REVIEWS_PAGE_ENABLED is unset,
                  and the sidebar link is hidden the same way (see Sidebar.jsx). */}
              <Route path="/bonsai-agents" element={<BonsaiAgents />} />
              <Route path="/new-matter" element={<NewMatterEntry />} />
              {/* Brief 77 Drift 1/2 — Conflict Search is the unified new-matter
                  funnel. Legacy routes redirect here (no dead routes, no 404s). */}
              <Route path="/conflict-search" element={<BonsaiProvider><ConflictsSearch /></BonsaiProvider>} />
              {/* BM-H393: alias routes must carry the query string through.
                  ConflictsSearch prefills six form fields from it, and four
                  call sites link here with parameters attached. A bare
                  <Navigate> dropped them and landed the fee earner on an
                  empty form with no error to explain why. */}
              <Route path="/conflicts-search" element={<PreserveQueryNavigate to="/conflict-search" />} />
              <Route path="/conflict-check" element={<PreserveQueryNavigate to="/conflict-search" />} />
              {/* BM-H452 Phase 1 — client register discovery (read only) */}
              <Route path="/client-register-discovery" element={<ClientRegisterDiscovery />} />
              <Route path="/conflict-check-health" element={<ConflictCheckHealth />} />
              <Route path="/conflict-check/:id" element={<ConflictCheckDetail />} />
              <Route path="/risk-assessment" element={<RiskAssessment />} />
              <Route path="/forf/:matterRef" element={<FoRFCombined />} />
              <Route path="/pre-caseminder" element={<PreCaseMinder />} />

              {/* Completions */}
              <Route path="/completions-book" element={<CompletionsBookDashboard />} />
              <Route path="/completions-book/new" element={<CompletionsBookForm />} />
              <Route path="/completions-book/:id" element={<CompletionsBookForm />} />
              <Route path="/pre-exchange-checklist" element={<PreExchangeChecklist />} />
              <Route path="/pre-completion-checklist" element={<PreCompletionChecklist />} />
              <Route path="/refinance-completion-checklist" element={<RefinanceCompletionChecklist />} />
              <Route path="/post-completion" element={<PostCompletionPage />} />
              <Route path="/completion-statement" element={<CompletionStatementPage />} />

              {/* Finance */}
              <Route path="/credit-control" element={<CreditControl />} />
              <Route path="/credit-control/:id" element={<CreditControlRecord />} />
              {/* Brief 52 (14 Jul 2026) — Finance: Trust Accounts Report (role-gated, read-only) */}
              <Route path="/trust-accounts-report" element={<TrustAccountsReport />} />
              <Route path="/client-account" element={<ClientAccount />} />
              <Route path="/invoice-request" element={<InvoiceRequest />} />
              <Route path="/payment-request" element={<PaymentRequest />} />
              <Route path="/accounts-instruction" element={<AccountsInstructionPage />} />
              <Route path="/send-sof-questionnaire" element={<SendSofQuestionnairePage />} />
              <Route path="/third-party-invoices" element={<ThirdPartyInvoices />} />
              <Route path="/invoice-tracker" element={<InvoiceTracker />} />
              {/* Brief 48 — Finance: Invoice Request <-> Credit Control relink admin (MD/director) */}
              <Route path="/finance/relink-invoice-requests" element={<RelinkInvoiceRequests />} />
              {/* BM-H4390 — Finance: every native client invoice (directors, accounts) */}
              <Route path="/finance/invoices" element={<ClientInvoices />} />
              {/* Brief 50 — Finance: Payment Request Tracker (accounts/director/MD) */}
              <Route path="/finance/payment-tracker" element={<PaymentTracker />} />
              {/* BM-H3488 -- the dashboard card and Mate nudge linked here, and it 404'd to the catch-all (which bounced to Bonsai Mail). Kept as an alias for links already clicked or bookmarked. */}
              <Route path="/payment-tracker" element={<PreserveQueryNavigate to="/finance/payment-tracker" />} />
              {/* time-recording-foundations — fee-earner billing rate admin (director/admin) */}
              <Route path="/finance/fee-earner-rates" element={<FeeEarnerRates />} />
              {/* time-recording-foundations Ticket 5 — every fee earner's own time recording */}
              <Route path="/my-time" element={<MyTime />} />
              {/* BM-H4253: Claude connector consent and disconnect */}
              <Route path="/connect-claude" element={<ConnectClaude />} />
              <Route path="/add-expense" element={<AddExpense />} />
              <Route path="/all-time-entries" element={<AllTimeEntries />} />

              {/* BM-H77 Phase-2 — Quoting Tool. Tracker at /quotes, builder at
                  /quotes/new (create) and /quotes/:ref/edit (edit draft),
                  read-only view at /quotes/:ref with state-driven actions. */}
              <Route path="/quotes" element={<Quotes />} />
              <Route path="/quotes/new" element={<QuoteBuilder />} />
              <Route path="/quotes/:ref/edit" element={<QuoteBuilder editMode />} />
              <Route path="/quotes/:ref" element={<QuoteView />} />

              {/* Global File Snap hub — feature-flagged cross-matter surface.
                  Wrapped in FileSnapFlagGuard (redirects when
                  FILE_SNAP_HUB_ENABLED is off), mirroring the ComplianceMinder
                  gate. */}
              <Route path="/file-snap" element={<FileSnapFlagGuard><FileSnapHub /></FileSnapFlagGuard>} />

              {/* Documents */}
              <Route path="/bonsai-pen" element={<BonsaiPen />} />
              <Route path="/bonsai-pen/tcs" element={<BonsaiPenTcs />} />
              <Route path="/bonsai-pen/docs" element={<BonsaiPenDocs />} />
              <Route path="/bonsai-pen/docs/:type" element={<BonsaiPenDocs />} />
              <Route path="/bonsai-pen/late-payment-calculator" element={<LatePaymentCalculator />} />
              <Route path="/bonsai-pen/late-payment" element={<LatePaymentLetters />} />
              <Route path="/bonsai-pen/late-payment/:workupId" element={<LatePaymentLetters />} />
              <Route path="/signatures" element={<SignatureManagement />} />
              <Route path="/client-doc-request" element={<ClientDocRequestPage />} />

              {/* Admin */}
              {/* BM-H626 (26 Sep 2026): one Directors Lounge, three tabs. The old
                  addresses forward to the matching tab. */}
              <Route path="/directors" element={<BonsaiProvider><DirectorsLounge /></BonsaiProvider>} />
              <Route path="/directors/analytics" element={<Navigate to="/directors?tab=analytics" replace />} />
              <Route path="/sop" element={<SOPPage />} />
              {/* BM-H4 — Guides Library */}
              <Route path="/guides" element={<GuidesLibrary />} />
              <Route path="/guides/:key" element={<GuideReader />} />
              <Route path="/admin/tools" element={<AdminTools />} />
              {/* BM-H314 — production state, legible in the app */}
              <Route path="/admin/system-status" element={<SystemStatus />} />
              <Route path="/admin/dashboard" element={<Navigate to="/directors?tab=health" replace />} />
              <Route path="/admin/bonsai-mail/failures" element={<BonsaiMailFailureConsole />} />
              <Route path="/admin/onboarding-link-health" element={<OnboardingLinkHealth />} />
              <Route path="/admin/ref-fixer" element={<RefFixerPage />} />
              <Route path="/admin/client-fixer" element={<ClientFixerPage />} />
              <Route path="/operator" element={<OperatorConsole />} />
              <Route path="/ob-email-log" element={<OBEmailLog />} />
              <Route path="/sent-email-log" element={<SentEmailLog />} />
              <Route path="/reports" element={<Reports />} />

              {/* Submissions & contacts */}
              <Route path="/submission/:id" element={<SubmissionDetail />} />
              {/* BM-H3488 -- payment tracker rows, backend notification action links and the MoA history tab use these older names; both fell to the catch-all. */}
              <Route path="/submissions/:id" element={<SubmissionDetail />} />
              <Route path="/payment-request/:id" element={<SubmissionDetail />} />
              <Route path="/contacts" element={<ContactsDirectory />} />
              <Route path="/documents/find" element={<FindDocument />} />
              <Route path="/contacts/:contactId" element={<ContactDetailPage />} />

              {/* File closure */}
              <Route path="/file-closure" element={<FileClosureForm />} />

              <Route path="/policy/library" element={<PolicyLibrary />} />
              <Route path="/policy/reader/:slug" element={<PolicyReader />} />
              <Route path="/policy/my" element={<MyPolicies />} />
              <Route path="/policy/forms" element={<NotificationForms />} />
              <Route path="/policy/registers" element={<RegistersForms />} />
              <Route path="/policy/registers/:ref" element={<RegisterFormDetail kind="register" />} />
              <Route path="/policy/forms/:ref" element={<RegisterFormDetail kind="form" />} />
              <Route path="/policy/inbox/mlco" element={<MLCOInbox />} />
              {/* 26 Oct 2026: folded into the Compliance Dashboard as a tab. */}
              <Route path="/policy/dashboard/mlco" element={<Navigate to="/compliance/dashboard?tab=aml" replace />} />

            </Route>

            {/* Bonsai Mail routes — standalone, NO AppLayout/sidebar */}
            <Route element={<BonsaiProvider><ErrorBoundary><Outlet /></ErrorBoundary></BonsaiProvider>}>
              <Route path="/mail" element={<BonsaiMailOnline />} />
              <Route path="/bonsai" element={<PreserveQueryNavigate to="/bonsai/mail" />} />
              <Route path="/bonsai/inbox" element={<PreserveQueryNavigate to="/bonsai/mail" />} />
              <Route path="/bonsai/mail" element={<BonsaiMailOnline />} />
              <Route path="/bonsai/chat" element={<BonsaiChat />} />
              <Route path="/bonsai/rules" element={<BonsaiRules />} />
              <Route path="/bonsai/activity" element={<BonsaiActivity />} />
              <Route path="/bonsai/deadlines" element={<BonsaiDeadlines />} />
              <Route path="/bonsai/deadlines/directors" element={<DeadlineDirectors />} />
              <Route path="/bonsai/filing/compliance" element={<FilingCompliance />} />
              <Route path="/bonsai/reports/weekly" element={<WeeklyReport />} />
              <Route path="/bonsai/time/dashboard" element={<TimeDashboard />} />
              <Route path="/bonsai/deleted-submissions" element={<DeletedSubmissions />} />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<CatchAllRedirect />} />
          </Routes>
        </AppRouter>
      </div>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
